<template>
    <div :class="containerClass">
        <Dialog
            v-model:visible="dialogVisible"
            :header="header"
            :position="position"
            :draggable="draggable"
            :resizable="resizable"
            :maximizable="maximizable"
            :modal="modal"
            :closeOnEscape="closeOnEscape"
            :dismissableMask="dismissableMask"
            :style="responsiveStyle"
            :breakpoints="responsiveBreakpoints"
            :closable="closable"
            @show="onShow"
            @hide="onHide"
            @maximize="onMaximize"
        >
            <template #header v-if="$slots.header">
                <slot name="header"></slot>
            </template>

            <slot></slot>

            <template #footer v-if="$slots.footer">
                <slot name="footer"></slot>
            </template>
        </Dialog>
    </div>
</template>

<script lang="ts">
import { defineComponent, computed, watch } from 'vue'
import type { PropType } from 'vue'
import Dialog from 'primevue/dialog'
import type { DialogPosition, ONEDialogProps } from './types'

export default defineComponent({
    name: 'ONEDialog',
    components: {
        Dialog
    },
    props: {
        modelValue: {
            type: Boolean as PropType<ONEDialogProps['modelValue']>,
            default: false
        },
        header: {
            type: String as PropType<ONEDialogProps['header']>,
            default: undefined
        },
        position: {
            type: String as PropType<DialogPosition>,
            default: undefined
        },
        draggable: {
            type: Boolean as PropType<ONEDialogProps['draggable']>,
            default: false
        },
        resizable: {
            type: Boolean as PropType<ONEDialogProps['resizable']>,
            default: false
        },
        maximizable: {
            type: Boolean as PropType<ONEDialogProps['maximizable']>,
            default: false
        },
        modal: {
            type: Boolean as PropType<ONEDialogProps['modal']>,
            default: true
        },
        closeOnEscape: {
            type: Boolean as PropType<ONEDialogProps['closeOnEscape']>,
            default: true
        },
        dismissableMask: {
            type: Boolean as PropType<ONEDialogProps['dismissableMask']>,
            default: false
        },
        rtl: {
            type: Boolean as PropType<ONEDialogProps['rtl']>,
            default: false
        },
        closable:{
            type: Boolean as PropType<ONEDialogProps['closable']>,
                default: true
        },
        style: {
            type: Object as PropType<ONEDialogProps['style']>,
            default: () => ({})
        },
        className: {
            type: String as PropType<ONEDialogProps['className']>,
            default: ''
        },
        breakpoints: {
            type: Object as PropType<ONEDialogProps['breakpoints']>,
            default: () => ({})
        },
        /**
         * PrimeVue's own Dialog is controlled with `visible`, so 142 call sites in
         * this codebase wrote `v-model:visible` / `:visible`. This component only
         * declared `modelValue`, so those bindings fell through as attributes and
         * the dialogs never opened. Both names are supported; whichever the caller
         * binds wins, and updates are emitted for both.
         */
        visible: {
            type: Boolean as PropType<boolean | undefined>,
            default: undefined
        }
    },
    emits: {
        'update:modelValue': (value: boolean) => true,
        'update:visible': (value: boolean) => true,
        'show': () => true,
        'hide': () => true,
        'maximize': (event: Event) => true
    },
    setup(props, { emit }) {
        const dialogVisible = computed({
            get: () => (props.visible !== undefined ? props.visible : props.modelValue),
            set: (value: boolean) => {
                // Emit only the event matching the prop the caller bound. Emitting both
                // made a parent that handles just one of them re-render without its
                // prop ever changing, which put <BaseTransition> into a
                // "Maximum recursive updates exceeded" loop.
                if (props.visible !== undefined) {
                    emit('update:visible', value)
                } else {
                    emit('update:modelValue', value)
                }
            }
        })

        // Auto-apply mobile-responsive breakpoints when none provided
        const responsiveBreakpoints = computed(() => {
            if (props.breakpoints && Object.keys(props.breakpoints).length > 0) {
                return props.breakpoints
            }
            // Default: 95vw on mobile screens
            return { '768px': '95vw' }
        })

        // Merge style with maxWidth to prevent overflow on mobile
        const responsiveStyle = computed(() => {
            return {
                ...props.style,
                maxWidth: props.style?.maxWidth || '95vw'
            }
        })

        const containerClass = computed(() => {
            return [
                'one-dialog',
                props.className,
                {
                    'one-dialog--rtl': props.rtl
                }
            ]
        })

        watch(() => props.modelValue, (newValue) => {
            if (newValue) {
                emit('show')
            } else {
                emit('hide')
            }
        }, { immediate: true })

        const onShow = () => {
            emit('show')
        }

        const onHide = () => {
            emit('hide')
        }

        const onMaximize = (event: Event) => {
            emit('maximize', event)
        }

        return {
            dialogVisible,
            containerClass,
            responsiveBreakpoints,
            responsiveStyle,
            onShow,
            onHide,
            onMaximize
        }
    }
})
</script>

<style lang="scss">
.one-dialog {
    &--rtl {
        direction: rtl;
    }
}
</style>
